import { z } from 'zod'
import type { ApiClient } from './client'
import { SearchQueryError } from './search'

/**
 * Connected signals — the backend job behind the Signals Storylines lens
 * (docs/signals.md §Storylines). Same job pipeline as search (search.ts):
 *
 *     POST /{clusterId}/wrk/api/job/invoke   job_type 'connectedsignals' -> TWO tasks
 *     GET  …/dispatcher/task/state/{id}      (searchApi.state)
 *     GET  …/dispatcher/task/result/{id}     graph_data, NOT paged
 *
 * The API builds `SELECT * FROM SIGNALS WHERE $Duration={duration}` itself
 * and chains dl.sql (`…-id-0`) -> dl.connectedsignals (`…-id-1`). The task
 * this module reads is data[1] — strictly, as the legacy console does
 * (data[0] lacks the tenant prefix on the multi-tenant backend).
 * Source-read 2026-10-06; live capture PENDING: docs/inventory/connected-signals-capture.md.
 */

/** Legacy `waitage` (signal-analyzer/index.js:161) — a constant, no UI. */
export const CONNECTED_SIGNALS_WEIGHT_FILTER = 10

/* ------------------------------------------------------------------ schemas */

const csTask = z.object({ id: z.string(), status: z.string().catch('') }).passthrough()

export const connectedSignalsInvokeResponse = z
  .object({
    status: z.string().catch(''),
    message: z.string().nullable().optional().catch(null),
    data: z.array(csTask).catch([]),
  })
  .passthrough()

/** Entity node ids are RAW values — an int `$TargetPort` arrives as a number. */
const csId = z.union([z.string(), z.number()])
export type CsId = z.output<typeof csId>

export const csSignalNode = z
  .object({
    id: z.string(),
    group: z.literal('signal'),
    weight: z.union([z.number(), z.string()]).nullable().catch(null),
    DetectionName: z.string().nullable().catch(null),
    DetectionTactic: z.string().nullable().catch(null),
    DetectionTechnique: z.string().nullable().catch(null),
    DetectionSubtechnique: z.string().nullable().catch(null),
    DetectionConfidence: z.union([z.string(), z.number()]).nullable().catch(null),
    DetectionSeverity: z.string().nullable().catch(null),
    CNAMTime: z.number().nullable().catch(null),
    count: z.number().catch(1),
    starttime: z.number().nullable().catch(null),
    endtime: z.number().nullable().catch(null),
    connected_sig_workbooks: z.record(z.array(z.unknown())).catch({}),
  })
  .passthrough()

export const csEntityNode = z
  .object({
    id: csId,
    group: z.enum(['suspect', 'target', 'compromised']),
    field: z.string().catch(''),
  })
  .passthrough()

export type CsSignalNode = z.output<typeof csSignalNode>
export type CsEntityNode = z.output<typeof csEntityNode>

/** One bad element drops itself, never the whole subgraph. */
const tolerantArray = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(item.nullable().catch(null))
    .catch([])
    .transform((a) => a.filter((x): x is z.output<T> => x !== null))

export const csSubgraph = z
  .object({
    total_weight: z.number(),
    nodes: tolerantArray(z.union([csSignalNode, csEntityNode])),
    links: tolerantArray(z.object({ source: csId, target: csId })),
    merge_ids: z.record(z.array(z.string())).catch({}),
    no_of_nodes: z.number().catch(0),
    no_of_signals: z.number().catch(0),
    no_of_suspects: z.number().catch(0),
    no_of_targets: z.number().catch(0),
    no_of_unique_signals: z.number().catch(0),
    no_of_compromised: z.number().catch(0),
    subgraph_name: z.string().catch('Attack pattern detected'),
    entity_name: z.string().nullable().catch(null),
    // `title_html` passes through and is NEVER read: raw entity values inside
    // a broken `<span className={…}>` (BACKEND-ASKS CB-63).
  })
  .passthrough()

export type CsSubgraph = z.output<typeof csSubgraph>

export const connectedSignalsResultResponse = z
  .object({
    status: z.string().catch(''),
    task_status: z.string().nullable().catch(null),
    message: z.string().nullable().optional().catch(null),
    execution_time: z.string().nullable().catch(null),
    /** Counts `result` (always 0 here), NOT subgraphs — never read (CB-63). */
    total_count: z.number().catch(0),
    /** null when the reply carries no `graph_data` array — the worker always sends one, so that is a failure. */
    graph_data: z.preprocess((v) => (Array.isArray(v) ? v : null), tolerantArray(csSubgraph).nullable()),
  })
  .passthrough()

export function isSignalNode(n: CsSignalNode | CsEntityNode): n is CsSignalNode {
  return n.group === 'signal'
}

/** Throws unless the task succeeded — `status: 'failed'` is how the result
 * endpoint answers for a still-running, failed or expired task — and unless
 * it carries a `graph_data` array (only `[]` means "no storylines"). */
export function readConnectedSignalsResult(
  res: z.output<typeof connectedSignalsResultResponse>,
): { subgraphs: CsSubgraph[]; executionTime: string | null } {
  const failed = res.status !== 'success' || /fail/i.test(res.task_status ?? '')
  if (failed) throw new Error(res.message || res.task_status || 'The connected-signals job did not finish.')
  if (res.graph_data === null) throw new Error(res.message || 'The connected-signals job returned no graph data.')
  return { subgraphs: res.graph_data, executionTime: res.execution_time }
}

/* ---------------------------------------------------------------------- api */

export interface ConnectedSignalsApi {
  invoke: (q: { clusterId: string; scopeId: string; duration: string; weightFilter: number }) => Promise<string>
  result: (q: { clusterId: string; taskId: string }) => Promise<{ subgraphs: CsSubgraph[]; executionTime: string | null }>
}

function localTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

export function createConnectedSignalsApi(client: ApiClient): ConnectedSignalsApi {
  return {
    invoke: async ({ clusterId, scopeId, duration, weightFilter }) => {
      const res = await client.request('POST', `/${clusterId}/wrk/api/job/invoke`, {
        body: {
          scope_id: scopeId,
          job_type: 'connectedsignals',
          job_execution: 'on-demand',
          query_timezone: localTimezone(),
          weight_filter: weightFilter,
          duration,
        },
        schema: connectedSignalsInvokeResponse,
      })
      if (res.status !== 'success') {
        throw new SearchQueryError(res.message || 'The connected-signals job did not start.', null, res)
      }
      const taskId = res.data[1]?.id
      if (!taskId) throw new SearchQueryError('The engine started no connected-signals task.', null, res)
      return taskId
    },
    result: async ({ clusterId, taskId }) =>
      readConnectedSignalsResult(
        await client.request('GET', `/${clusterId}/wrk/api/dispatcher/task/result/${taskId}`, {
          schema: connectedSignalsResultResponse,
        }),
      ),
  }
}
